import { useState } from 'react';
import { toast } from 'sonner';
import { KIInitScreen } from './components/KIInitScreen';
import { KIDashboard } from './components/KIDashboard';
import { KIResults } from './components/KIResults';
import { calculateBase44Frequency, calculateDivergence } from './lib/base44';
import { fetchCompleteEtymology } from './lib/etymologyAPI';
import { generateOracleInsight } from './lib/oracleAPI';
import './styles/design-tokens.css';

interface SearchResult {
  word: string;
  protoRoot: string;
  originLanguage: string;
  originalMeaning: string;
  modernDefinition: string;
  modernUsage: string;
  ancientFrequency: number;
  modernFrequency: number;
  divergence: number;
  divergencePercentage: number;
  inversionFlag: boolean;
  inversionType?: string;
  oracleSummary: string;
}

function App() {
  const [initialized, setInitialized] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (query: string) => {
    setIsLoading(true);
    try {
      // Fetch etymology and modern definition
      const etymologyData = await fetchCompleteEtymology(query);

      // Calculate Base 44 frequencies
      const ancientFreq = calculateBase44Frequency(etymologyData.protoRoot);
      const modernFreq = calculateBase44Frequency(query);

      // Calculate divergence
      const divergenceData = calculateDivergence(
        ancientFreq.coreFrequency,
        modernFreq.coreFrequency
      );

      // Generate Oracle Insight
      const oracleInsight = await generateOracleInsight(
        query,
        etymologyData.protoRoot,
        etymologyData.originalMeaning,
        etymologyData.modernDefinition,
        ancientFreq.coreFrequency,
        modernFreq.coreFrequency,
        divergenceData.divergence
      );

      setSearchResult({
        word: query,
        protoRoot: etymologyData.protoRoot,
        originLanguage: etymologyData.originLanguage,
        originalMeaning: etymologyData.originalMeaning,
        modernDefinition: etymologyData.modernDefinition,
        modernUsage: etymologyData.modernUsage,
        ancientFrequency: ancientFreq.coreFrequency,
        modernFrequency: modernFreq.coreFrequency,
        divergence: divergenceData.divergence,
        divergencePercentage: divergenceData.percentage,
        inversionFlag: oracleInsight.inversionFlag,
        inversionType: oracleInsight.inversionType,
        oracleSummary: oracleInsight.summary,
      });

      toast.success(`${query} decoded successfully`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to decode word. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!initialized) {
    return <KIInitScreen onComplete={() => setInitialized(true)} />;
  }

  return (
    <div className="min-h-screen bg-stone-void">
      <KIDashboard onSearch={handleSearch} isLoading={isLoading} />
      {searchResult && (
        <div className="max-w-4xl mx-auto px-4 py-12">
          <KIResults {...searchResult} isLoading={isLoading} />
        </div>
      )}
    </div>
  );
}

export default App;